import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import {
  Observable,
  BehaviorSubject,
  combineLatest,
  Subscription,
  timer
} from 'rxjs';
import { isEqual } from 'lodash';
import {
  map,
  filter,
  distinctUntilChanged,
  switchMap,
  tap,
  withLatestFrom,
  throttleTime,
  delayWhen,
  publishReplay,
  refCount
} from 'rxjs/operators';
import {
  Load,
  Reset,
  SetUseDefaultVersion,
  SetVersion
} from '../state/deal/actions';
import { DealState } from '../state/deal/state';
import { getDealStoreState } from '../state/reducer';
import {
  DealVersionSelector,
  DealInformation,
  DealUserRole
} from '../types/deal.types';
import { dealRoleForUser } from '../helpers/issue/user-role';
import { SessionFacade } from './session.facade';
import {
  LiveUpdateService,
  THROTTLE_DELAY,
  getMessageDelay
} from '../services/live-update.service';
import { PendoDealShowLoaded } from '../state/pendo/actions';

@Injectable()
export class DealFacade {
  public dealState$: Observable<DealInformation>;

  private dealId$ = new BehaviorSubject<string>(undefined);

  private liveUpdateSubscription$: Subscription;
  public dealData$: Observable<DealState> = this.store.pipe(
    select(getDealStoreState),
    map((state: DealState) => state)
  );

  constructor(
    private store: Store<Record<string, unknown>>,
    private sessionFacade: SessionFacade,
    private liveUpdateService: LiveUpdateService
  ) {
    this.dealState$ = this.trackLiveUpdates(
      combineLatest(
        this.store.pipe(select(getDealStoreState)),
        this.dealId$.pipe(distinctUntilChanged())
      ).pipe(
        filter(([dealState, dealId]: [DealState, string]) =>
          this.checkDealLoaded(dealState, dealId)
        ),
        switchMap(([dealState, dealId]: [DealState, string]) =>
          this.sessionFacade.session$.pipe(
            map((session) => {
              if (dealState.loaded) {
                const deal = dealState.deal[dealState.selectedVersion];
                const availableVersions = Object.values(
                  DealVersionSelector
                ).filter((selector) =>
                  Boolean(dealState.deal[selector])
                ) as DealVersionSelector[];
                return {
                  dealId,
                  deal,
                  selectedVersion: dealState.selectedVersion,
                  availableVersions,
                  userRole: dealRoleForUser(deal, session),
                  hasWorkingVersion: availableVersions.reduce(
                    (acc, curr) =>
                      acc ||
                      dealRoleForUser(dealState.deal[curr], session) ===
                        DealUserRole.bookrunner,
                    false
                  ),
                  loaded: true,
                  loading: false,
                  persona: session.persona,
                  error: undefined
                };
              } else {
                return {
                  dealId,
                  deal: undefined,
                  selectedVersion: undefined,
                  availableVersions: [],
                  userRole: undefined,
                  loaded: false,
                  loading: dealState.loading,
                  hasWorkingVersion: false,
                  persona: session.persona,
                  error: dealState.error
                };
              }
            }),
            distinctUntilChanged(isEqual)
          )
        )
      )
    ).pipe(publishReplay(1), refCount());
  }

  public setDealId(dealId: string) {
    if (this.dealId$.getValue() !== dealId) {
      if (dealId) {
        this.store.dispatch(new PendoDealShowLoaded(dealId));
      }
      this.dealId$.next(dealId);
    }
  }

  public setDealVersion(dealVersion: DealVersionSelector) {
    this.store.dispatch(new SetVersion(dealVersion));
  }

  public setUseDefaultVersion() {
    this.store.dispatch(new SetUseDefaultVersion());
  }

  private trackLiveUpdates<T>(source: Observable<T>) {
    return new Observable<T>((observer) => {
      // This is fired the first time someone subscribes to dealState$
      this.startLiveUpdateListener();

      const subscription = source.subscribe(observer);
      return () => {
        // This is fired when the last subscriber unsubscribes from dealState$
        subscription.unsubscribe();
        this.endLiveUpdateListener();
      };
    });
  }

  private checkDealLoaded(state: DealState, dealId: string) {
    if (state.deal && state.deal.id !== dealId) {
      this.store.dispatch(new Reset());
      return false;
    } else if (!state.loaded && !state.loading && !!dealId) {
      if (!state.error) {
        this.store.dispatch(new Load(dealId));
      } else {
        this.store.dispatch(new Reset());
        this.setDealId(null);
      }
      return false;
    } else {
      return true;
    }
  }

  private startLiveUpdateListener() {
    if (!this.liveUpdateSubscription$) {
      console.log('Subscribing to Deal Live Updates');
      this.liveUpdateSubscription$ = this.liveUpdateService
        .startPolling()
        .pipe(
          withLatestFrom(this.dealId$),
          filter(([msg, dealId]) => msg.DealId === dealId),
          throttleTime(THROTTLE_DELAY, undefined, {
            leading: true,
            trailing: true
          }),
          delayWhen(([msg, _dealId]) => timer(getMessageDelay(msg))),
          tap(([_msg, dealId]) => this.store.dispatch(new Load(dealId)))
        )
        .subscribe();
    }
  }

  private endLiveUpdateListener() {
    if (this.liveUpdateSubscription$) {
      console.log('Unsubscribing from Deal Live Updates');
      this.liveUpdateSubscription$.unsubscribe();
      this.liveUpdateSubscription$ = null;
    }
  }
}
